import { hasRole, useUser } from "@procurement-agents/auth";
import { Badge, Button } from "@procurement-agents/ui";
import { ApiError, type TemplateVersion, type TemplateVersionSummary, type VersionDiff } from "@procurement-agents/scope-api";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useScopeApi } from "../api";
import { relativeTime } from "../labels";
import { SectionCard } from "./SectionCard";
import { VersionDiffView } from "./VersionDiff";

/** The published version as cards, with "Create a new version" / "Open draft", and the version history with a diff viewer. */
export function TemplateView({ referenceTables }: { referenceTables: string[] }) {
  const { id = "" } = useParams();
  const { api, toast } = useScopeApi();
  const user = useUser();
  const nav = useNavigate();
  const [pub, setPub] = useState<TemplateVersion | null | undefined>(undefined);
  const [versions, setVersions] = useState<TemplateVersionSummary[]>([]);
  const [pick, setPick] = useState<[number, number] | null>(null);
  const [diff, setDiff] = useState<VersionDiff | null>(null);
  const canDraft = hasRole(user, "procurement_user", "sme");
  useEffect(() => {
    api.getTemplate(id).then(setPub).catch((e) => setPub(e instanceof ApiError && e.status === 404 ? null : undefined));
    if (canDraft) api.templateVersions(id).then(setVersions).catch(() => setVersions([]));
  }, [api, id, canDraft]);
  useEffect(() => { if (pick) api.diffVersions(id, pick[0], pick[1]).then(setDiff).catch(() => setDiff(null)); else setDiff(null); }, [api, id, pick]);
  const draft = versions.find((v) => v.status === "draft");
  const start = async () => { try { await api.startDraft(id); nav("draft"); } catch (e) { toast(String((e as Error).message), "warn"); } };
  // QA review fixes (5 Oct): a published template can be retired from here (the server refuses retiring the catch-all)
  const [retiring, setRetiring] = useState(false);
  const canRetire = hasRole(user, "procurement_user") && !!pub;
  const retire = async () => { try { await api.retireTemplate(id); toast("Template retired; new requests no longer use it."); nav(".."); } catch (e) { toast(e instanceof ApiError ? e.detail : String(e), "warn"); } };
  if (pub === undefined) return <p className="muted">Loading…</p>;
  return (
    <div className="page templates">
      <Link to=".." className="back-link">← All templates</Link>
      <header className="row between">
        <div><h2>{pub?.name ?? id}</h2>{pub && <p className="muted small">Published v{pub.version} {relativeTime(pub.published_at)} by {pub.published_by} · categories: {pub.categories.join(", ") || "—"}{pub.change_note ? ` · "${pub.change_note}"` : ""}</p>}</div>
        <span className="row">
          {canRetire && (retiring
            ? <span className="row"><span className="small">Retire it? New requests stop using it; requests already under way keep their version.</span><Button variant="danger" onClick={() => void retire()}>Yes, retire</Button><Button variant="quiet" onClick={() => setRetiring(false)}>Cancel</Button></span>
            : <Button variant="quiet" onClick={() => setRetiring(true)}>Retire…</Button>)}
          {canDraft && (draft ? <Button variant="primary" onClick={() => nav("draft")}>Open draft v{draft.version}</Button> : <Button variant="primary" onClick={start}>Create a new version</Button>)}
        </span>
      </header>
      {pub === null && <p className="muted">No published version yet{draft ? "; a draft is in progress." : "."}</p>}
      {pub && pub.sections.map((s) => <SectionCard key={s.id} section={s} siblings={pub.sections} role="business" owned={false} editable={false} domains={[]} referenceTables={referenceTables} />)}
      {canDraft && versions.length > 0 && (
        <section className="history">
          <h3>History</h3>
          <table className="table"><thead><tr><th>Version</th><th>Status</th><th>Change note</th><th>By</th><th>When</th><th /></tr></thead>
            <tbody>{versions.slice().reverse().map((v) => (
              <tr key={v.version}><td>v{v.version}</td><td><Badge tone={v.status === "published" ? "good" : v.status === "draft" ? "info" : "neutral"}>{v.status}</Badge></td><td>{v.change_note || <span className="muted">—</span>}</td><td className="small">{v.published_by ?? v.updated_by ?? v.created_by}</td><td className="small muted">{relativeTime(v.published_at ?? v.updated_at)}</td>
                <td>{pub && v.version !== pub.version && <button type="button" className="link small" onClick={() => setPick([Math.min(v.version, pub.version), Math.max(v.version, pub.version)])}>Compare with v{pub.version}</button>}</td></tr>
            ))}</tbody></table>
          {pick && <div className="card"><h4>v{pick[0]} → v{pick[1]}</h4>{diff ? <VersionDiffView diff={diff} /> : <p className="muted">Loading…</p>}</div>}
        </section>
      )}
    </div>
  );
}
